import { ReactNode, useMemo } from 'react';
import { defineAbilitiesFor, PlatformAction, PlatformSubject } from '@/lib/abilities';
import { useAuthStore } from '@/store/authStore';

/**
 * Declarative permission gate. Renders `children` only if the current
 * user can perform the specified action on the specified subject.
 *
 * Mirror of the backend platformRequireAbility middleware for UI
 * gating. The server is still authoritative — a hidden button that
 * the user could still hit via the API is protected server-side.
 */
export function Can({
    action,
    subject,
    fallback = null,
    children,
}: {
    action: PlatformAction;
    subject: PlatformSubject;
    fallback?: ReactNode;
    children: ReactNode;
}): ReactNode {
    const role = useAuthStore((s) => s.user?.role);
    const ability = useMemo(() => defineAbilitiesFor(role), [role]);
    return ability.can(action, subject) ? <>{children}</> : <>{fallback}</>;
}
